// ===========================================
// Permission Slips API
// ===========================================
// Three audiences, three clients:
//   - permissionSlipApi        admin  (/admin/permission-slips)
//   - permissionSlipParentApi  parent (/parents/permission-slips, session auth)
//   - permissionSlipPublicApi  magic link (/public/permission-slips, NO auth)
//
// ⚠️ DOUBLE UNWRAP. `apiClient` strips the axios wrapper (`res.data`), and the
// backend envelope `{ status, data }` is stripped again here — so hooks receive
// the bare payload. Miss the second `.then` and every consumer sees
// `{ status, data }` instead of the thing it wanted.

import { apiClient, publicClient } from '../services/api';

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// TYPES
// ===========================================

export type PermissionSlipStatus = 'DRAFT' | 'SCHEDULED' | 'SENT' | 'FAILED' | 'CLOSED';
export type PermissionSlipResponseStatus = 'PENDING' | 'SIGNED' | 'DECLINED';
export type SignatureMethod = 'DRAWN' | 'TYPED';

export interface PermissionSlipSummary {
  id: string;
  title: string;
  body: string;
  deadline: string | null;
  status: PermissionSlipStatus;
  requiresMediaConsent: boolean;
  requiresMedicalConsent: boolean;
  requiresTransportConsent: boolean;
  requiresDietaryInfo: boolean;
  Event?: { id: string; title: string; startDateTime?: string } | null;
}

/**
 * What the parent portal receives for one slip (#688): exactly the summary fields,
 * as the server sends an explicit projection. NOT `PermissionSlip`, which is the
 * admin shape (audience arrays, channels, scheduling) a parent must never get.
 */
export type ParentPermissionSlip = PermissionSlipSummary;

export interface PermissionSlip extends PermissionSlipSummary {
  schoolId: string;
  eventId: string | null;
  visibility: string;
  targetFamilies: string[];
  targetClasses: string[];
  targetAgeGroups: string[];
  targetProgramLevels: string[];
  channels: string[];
  scheduledFor: string | null;
  sentAt: string | null;
  lastSentAt: string | null;
  createdAt: string;
  _count?: { permissionSlipResponses: number };
}

/**
 * A per-student response.
 *
 * NOTE the absent fields: `signatureSvg`, `ipAddress` and `userAgent` are
 * deliberately NOT projected by the API. The signature is viewable only through
 * the authenticated PDF download route.
 */
export interface PermissionSlipResponse {
  id: string;
  status: PermissionSlipResponseStatus;
  mediaConsent: boolean | null;
  medicalConsent: boolean | null;
  transportConsent: boolean | null;
  dietaryNotes: string | null;
  parentNotes: string | null;
  /**
   * True when the viewer lacks `people_students.read` AND the parent actually
   * supplied health text — so a blank `dietaryNotes` means "hidden from you",
   * not "the parent left it empty".
   *
   * The distinction matters: someone arranging catering who reads an empty
   * field as "no allergies" would be drawing a dangerous conclusion from a
   * permission boundary. Absent on responses where nothing was redacted.
   */
  healthDataRedacted?: boolean;
  signedByName: string | null;
  signedAt: string | null;
  signedPdfUrl: string | null;
  createdAt?: string;
  Student: { id: string; firstName: string; lastName: string; familyId?: string };
  PermissionSlip?: PermissionSlipSummary;
}

export interface SignSlipInput {
  studentId: string;
  mediaConsent?: boolean;
  medicalConsent?: boolean;
  transportConsent?: boolean;
  dietaryNotes?: string | null;
  parentNotes?: string | null;
  signatureMethod: SignatureMethod;
  signatureSvg?: string;
  signatureText?: string;
  signatureFontKey?: string;
  signedByName: string;
}

export interface PermissionSlipInput {
  eventId?: string | null;
  title: string;
  body: string;
  deadline?: string | null;
  visibility?: string;
  targetFamilies?: string[];
  targetClasses?: string[];
  targetAgeGroups?: string[];
  targetProgramLevels?: string[];
  requiresMediaConsent?: boolean;
  requiresMedicalConsent?: boolean;
  requiresTransportConsent?: boolean;
  requiresDietaryInfo?: boolean;
  channels?: string[];
  scheduledFor?: string | null;
}

/** Preview / test-send input (#53): unsaved editor values + which email and wording. */
export interface SlipEmailRequest {
  title?: string;
  body?: string;
  /** `YYYY-MM-DD`, or `null` for no deadline; omit to use the saved one. */
  deadline?: string | null;
  /** A first request (first send, Refresh audience) or a Remind. */
  kind: 'REQUEST' | 'REMINDER';
  /** The wording for a contact with a login, or for one without (a contact 2). */
  hasLogin: boolean;
}

export interface SlipResponseStats {
  PENDING: number;
  SIGNED: number;
  DECLINED: number;
  total: number;
}

export interface StudentMedicalInfo {
  allergies: string | null;
  medicalNotes: string | null;
  dietaryRequirements: string | null;
}

// ===========================================
// ADMIN
// ===========================================

export const permissionSlipApi = {
  list: (filters?: { status?: PermissionSlipStatus; eventId?: string }) =>
    apiClient
      .get<ApiResponse<PermissionSlip[]>>('/admin/permission-slips', { params: filters })
      .then((res) => res.data),

  get: (id: string) =>
    apiClient
      .get<ApiResponse<PermissionSlip>>(`/admin/permission-slips/${id}`)
      .then((res) => res.data),

  stats: (id: string) =>
    apiClient
      .get<ApiResponse<SlipResponseStats>>(`/admin/permission-slips/${id}/stats`)
      .then((res) => res.data),

  responses: (id: string) =>
    apiClient
      .get<ApiResponse<PermissionSlipResponse[]>>(`/admin/permission-slips/${id}/responses`)
      .then((res) => res.data),

  create: (data: PermissionSlipInput) =>
    apiClient
      .post<ApiResponse<PermissionSlip>>('/admin/permission-slips', data)
      .then((res) => res.data),

  update: (id: string, data: Partial<PermissionSlipInput>) =>
    apiClient
      .patch<ApiResponse<PermissionSlip>>(`/admin/permission-slips/${id}`, data)
      .then((res) => res.data),

  remove: (id: string) =>
    apiClient
      .delete<ApiResponse<{ id: string }>>(`/admin/permission-slips/${id}`)
      .then((res) => res.data),

  schedule: (id: string, scheduledFor: string) =>
    apiClient
      .post<ApiResponse<PermissionSlip>>(`/admin/permission-slips/${id}/schedule`, { scheduledFor })
      .then((res) => res.data),

  send: (id: string) =>
    apiClient
      .post<ApiResponse<{ claimed: boolean; reason?: string }>>(
        `/admin/permission-slips/${id}/send`
      )
      .then((res) => res.data),

  close: (id: string) =>
    apiClient
      .post<ApiResponse<PermissionSlip>>(`/admin/permission-slips/${id}/close`)
      .then((res) => res.data),

  /** Chase only families who have not responded. Signed/declined are untouched. */
  remind: (id: string) =>
    apiClient
      .post<ApiResponse<{ sent: number; skipped: number; failed: number; families: number }>>(
        `/admin/permission-slips/${id}/remind`
      )
      .then((res) => res.data),

  /** Re-resolve the audience and contact only newly-matching students. */
  refreshAudience: (id: string) =>
    apiClient
      .post<ApiResponse<{ added: number; families: number; sent: number }>>(
        `/admin/permission-slips/${id}/refresh-audience`
      )
      .then((res) => res.data),

  /**
   * The slip's email as a family would get it (#53), rendered by the same code
   * as the real send. Draft fields are the editor's UNSAVED values; an absent
   * field uses the saved one. The link is a sample, never a working one.
   */
  emailPreview: (id: string, input: SlipEmailRequest) =>
    apiClient
      .post<ApiResponse<{ subject: string; html: string }>>(
        `/admin/permission-slips/${id}/email-preview`,
        input
      )
      .then((res) => res.data),

  /** Send that email to the signed-in admin's OWN address only. */
  sendTestEmail: (id: string, input: SlipEmailRequest) =>
    apiClient
      .post<ApiResponse<{ sent: boolean; subject: string; to: string }>>(
        `/admin/permission-slips/${id}/send-test`,
        input
      )
      .then((res) => res.data),

  /** See permissionSlipParentApi.fetchPdf — same reasons, admin route. */
  fetchPdf: (responseId: string): Promise<Blob> =>
    apiClient
      .getRaw<Blob>(`/admin/permission-slips/responses/${responseId}/pdf`, {
        responseType: 'blob',
      })
      // getRaw deliberately returns the whole axios response (headers etc.);
      // the caller only needs the body.
      .then((res) => res.data),
};

// ===========================================
// PARENT
// ===========================================
//
// familyId is NEVER sent — the server derives it from the session. A familyId
// in a request body is ignored by design.

export const permissionSlipParentApi = {
  list: () =>
    apiClient
      .get<ApiResponse<PermissionSlipResponse[]>>('/parents/permission-slips')
      .then((res) => res.data),

  pendingCount: () =>
    apiClient
      .get<ApiResponse<{ count: number }>>('/parents/permission-slips/pending-count')
      .then((res) => res.data),

  get: (slipId: string) =>
    apiClient
      .get<ApiResponse<{ slip: ParentPermissionSlip; responses: PermissionSlipResponse[] }>>(
        `/parents/permission-slips/${slipId}`
      )
      .then((res) => res.data),

  sign: (slipId: string, data: SignSlipInput) =>
    apiClient
      .post<ApiResponse<PermissionSlipResponse>>(
        `/parents/permission-slips/${slipId}/sign`,
        data
      )
      .then((res) => res.data),

  decline: (slipId: string, studentId: string, parentNotes?: string) =>
    apiClient
      .post<ApiResponse<PermissionSlipResponse>>(
        `/parents/permission-slips/${slipId}/decline`,
        { studentId, parentNotes }
      )
      .then((res) => res.data),

  getMedical: (studentId: string) =>
    apiClient
      .get<ApiResponse<StudentMedicalInfo | null>>(
        `/parents/permission-slips/students/${studentId}/medical`
      )
      .then((res) => res.data),

  updateMedical: (studentId: string, data: Partial<StudentMedicalInfo>) =>
    apiClient
      .patch<ApiResponse<StudentMedicalInfo>>(
        `/parents/permission-slips/students/${studentId}/medical`,
        data
      )
      .then((res) => res.data),

  /**
   * Fetch the signed PDF as a Blob.
   *
   * ⚠️ NOT an <a href>. Two reasons, both fatal to a plain link:
   *   1. The school slug and /api/v1 prefix are injected by the axios
   *      interceptor per request — a browser navigation skips that entirely and
   *      lands on the SPA catch-all, returning index.html instead of a PDF.
   *   2. Auth is a Bearer token from localStorage, not a cookie, so a plain
   *      navigation cannot carry it and the route 401s.
   * Going through the api client solves both; the caller turns the Blob into an
   * object URL.
   */
  fetchPdf: (responseId: string): Promise<Blob> =>
    apiClient
      .getRaw<Blob>(`/parents/permission-slips/responses/${responseId}/pdf`, {
        responseType: 'blob',
      })
      .then((res) => res.data),
};

// ===========================================
// PUBLIC (magic link — no auth)
// ===========================================

/**
 * School branding for the unauthenticated signing page.
 *
 * A parent arriving from an email has no portal shell around them and no login
 * to confirm where they are — the school's name, logo and colours are what make
 * a consent form look like it belongs to the school rather than a phishing
 * page. Resolved server-side from `School.branding` + `schoolInfo.logoUrl`
 * (schoolChrome.service.ts): colours always carry a value (brand defaults when
 * the school set none); the logo may be null.
 */
export interface PublicSlipSchool {
  name: string;
  logoUrl: string | null;
  primaryColor: string | null;
  secondaryColor: string | null;
}

export const permissionSlipPublicApi = {
  get: (token: string) =>
    publicClient
      .get<
        ApiResponse<{
          school: PublicSlipSchool;
          slip: PermissionSlipSummary;
          responses: PermissionSlipResponse[];
        }>
      >(`/public/permission-slips/${token}`)
      .then((res) => res.data),

  sign: (token: string, data: SignSlipInput) =>
    publicClient
      .post<ApiResponse<PermissionSlipResponse>>(`/public/permission-slips/${token}/sign`, data)
      .then((res) => res.data),

  decline: (token: string, studentId: string, parentNotes?: string) =>
    publicClient
      .post<ApiResponse<PermissionSlipResponse>>(`/public/permission-slips/${token}/decline`, {
        studentId,
        parentNotes,
      })
      .then((res) => res.data),
};
